import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * JourneyStepSkip, lets a user skip the current journey step so the next one
 * unlocks. Skipping affects navigation only; it does NOT count as completing
 * the step for the final FuturePath Report. Hidden for parents and once the
 * step is already done/skipped.
 */
export default function JourneyStepSkip({ step, nextPath, nextLabel }) {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'parent') return;
    axios.get(`${API}/journey/progress`, getAuthHeader ? getAuthHeader() : {})
      .then((r) => setProgress(r.data))
      .catch(() => {});
  }, [user, getAuthHeader]);

  if (!user || user.role === 'parent') return null;
  if (progress && progress[step]) return null; // already complete/skipped

  const handleSkip = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/journey/skip`, { step }, getAuthHeader ? getAuthHeader() : {});
      toast.success(`Skipped for now, ${nextLabel} is unlocked. You can come back anytime.`);
      navigate(nextPath);
    } catch {
      toast.error('Could not skip this step. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-end mb-4" data-testid="journey-step-skip">
      <button
        onClick={handleSkip}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-slate-200 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
        data-testid="journey-skip-btn"
      >
        Skip for now <SkipForward className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
